<template>
    <v-card :loading="$wait.waiting(`approve-contract-${approval.id}`) || $wait.waiting(`decline-contract-${approval.id}`)"
            flat
            tile
            key="approvalCard"
            min-width="100%"
            class="mb-2 overflow-hidden nested"
            :class="{ 'approved': approval.approved, 'declined': approval.declined }">

        <div class="pt-1 px-3 pb-5">
            <v-row no-gutters>
                <v-col cols="auto" class="align-self-center mr-3">
                    <user-avatar color="primary" :user="approval.user"></user-avatar>
                </v-col>
                <v-col>
                    <h2 class="headline font-weight-bold mb-1">
                        AB {{ approval.ab_number }}
                    </h2>
                    <p class="body-1 mb-0">
                        {{ $vuetify.lang.t('$vuetify.approval.submitted_by_on', approval.user.first_name, approval.user.last_name, date(approval.created_at)) }}
                    </p>
                </v-col>
            </v-row>

            <component :is="transition" mode="out-in">
                <v-container pa-0 v-if="approval.approved">
                    <v-icon class="mb-2 document-icon">mdi-file-check-outline</v-icon>
                    <p class="ma-0">
                        {{ $vuetify.lang.t('$vuetify.approval.contract_claim_approved', approval.ab_number) }}
                    </p>
                </v-container>

                <v-container pa-0 v-else-if="approval.declined">
                    <v-icon class="mb-2 document-icon">mdi-file-cancel-outline</v-icon>
                    <p class="ma-0">
                        {{ $vuetify.lang.t('$vuetify.approval.contract_claim_declined', approval.ab_number) }}
                    </p>
                </v-container>

                <v-container pa-0 v-else-if="!markedAsDeclined"
                             key="approvalCard"
                             class="body-1">

                    <p class="mt-4 mb-0 font-weight-bold">
                        {{ approval.model_name }}
                    </p>
                    <p class="mb-0 font-weight-bold">
                        <template v-if="approval.vin">
                            {{ approval.vin }}
                        </template>
                        <template v-else>
                            {{ $vuetify.lang.t('$vuetify.widgets.transactions.empty_vin') }}
                        </template>
                    </p>
                    <p class="mb-0 font-weight-bold" v-if="approval.commission">
                        {{ $vuetify.lang.t('$vuetify.form_items.commission') }}: {{ approval.commission }}
                    </p>
                    <p class="mb-0">
                        {{ $vuetify.lang.t('$vuetify.form_items.contract_date') }}: {{ approval.order_date_customer | date }}
                    </p>
                    <p class="mb-0">
                        {{ $vuetify.lang.t('$vuetify.form_items.potential_delivery_date') }}: {{ approval.potential_delivery_date | date }}
                    </p>
                    <p class="mb-4">
                        {{ approval.payment_type }}
                    </p>

                    <v-row no-gutters align="center">
                        <v-col cols="6" class="pr-1">
                            <v-btn block
                                   tile
                                   depressed
                                   color="success"
                                   @click="approve(approval.id)"
                                   :disabled="$wait.waiting(`approve-contract-${approval.id}`) || $wait.waiting(`decline-contract-${approval.id}`)"
                                   :loading="$wait.waiting(`approve-contract-${approval.id}`)">
                                {{ $vuetify.lang.t('$vuetify.buttons.approve') }}
                            </v-btn>
                        </v-col>
                        <v-col cols="6" class="pl-1">
                            <v-btn block
                                   tile
                                   depressed
                                   color="error"
                                   @click="handleDeclineBtnClick"
                                   :disabled="$wait.waiting(`approve-contract-${approval.id}`) || $wait.waiting(`decline-contract-${approval.id}`)"
                                   :loading="$wait.waiting(`decline-contract-${approval.id}`)">
                                {{ $vuetify.lang.t('$vuetify.buttons.decline') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container pa-0
                             v-else-if="declineReasons"
                             key="declineCard">
                    <v-icon class="mb-2 document-icon">mdi-file-cancel-outline</v-icon>
                    <p class="mb-2 pb-0 subtitle-2">
                        {{ $vuetify.lang.t('$vuetify.approval.decline_reason') }}
                    </p>

                    <v-radio-group v-model="chosenReason" :mandatory="true">
                        <v-radio v-for="reason in reasons"
                                 color="white"
                                 :key="reason.id"
                                 :label="reason.reason"
                                 :value="reason.id"></v-radio>
                    </v-radio-group>

                    <v-row no-gutters align="center">
                        <v-col cols="6" class="pr-1">
                            <v-btn icon
                                   class="back-btn"
                                   @click="transitionDirection(false); markedAsDeclined = false"
                                   :disabled="$wait.waiting(`approve-participant-${approval.id}`) || $wait.waiting(`decline-participant-${approval.id}`)">
                                <v-icon>mdi-arrow-left-circle-outline</v-icon>
                            </v-btn>
                        </v-col>
                        <v-col cols="6" class="pl-1">
                            <v-btn block
                                   tile
                                   depressed
                                   color="error"
                                   @click="decline(approval.id, chosenReason)"
                                   :disabled="$wait.waiting(`approve-contract-${approval.id}`) || $wait.waiting(`decline-contract-${approval.id}`)"
                                   :loading="$wait.waiting(`decline-contract-${approval.id}`)">
                                {{ $vuetify.lang.t('$vuetify.buttons.decline') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </component>
        </div>
    </v-card>
</template>

<script>
    import UserAvatar from "../UserAvatar";

    export default {
        name: 'approve-contract-card',
        components: { UserAvatar },
        data() {
            return {
                markedAsDeclined: false,
                chosenReason: null,
                transition: 'v-slide-left-transition',
            }
        },
        methods: {
            date(date) {
                return this.$options.filters.date(date);
            },
            transitionDirection(bool) {
                this.transition = bool ? 'v-slide-left-transition' : 'v-slide-right-transition';
            },
            handleDeclineBtnClick() {
                if (this.declineReasons) {
                    this.transitionDirection(true);
                    this.markedAsDeclined = true;
                } else {
                    this.decline(this.approval.id);
                }
            },
        },
        props: {
            approval: {
                required: true,
                type: Object,
            },
            reasons: {
                required: true,
                type: Array,
            },
            approve: {
                required: true,
                type: Function,
            },
            decline: {
                required: true,
                type: Function,
            },
        },
        computed: {
            declineReasons() {
                return this.reasons.length > 0;
            },
        },
    }
</script>
